/* eslint-disable no-await-in-loop */
import {
  ArchivedState,
  Company,
  ContainerUpdateForeignNationalMutation,
  Currency,
  CurrencyPeriod,
  FamilyRelationship,
  FamilyRelationshipType,
  ForeignNational,
  GetForeignNationalQuery,
  GetRelativesQuery,
  ListForeignNationalsByVisaExpirationQuery,
  ListForeignNationalsQuery,
  NationalOnboardingSections,
  PersonType,
  SearchableForeignNationalFilterInput,
  SearchForeignNationalNamesQuery,
  VisaStatus,
} from 'API';
import * as queries from 'graphql/queries';
import * as mutations from 'graphql/mutations';
import {
  customGetForeignNational, getRelatives, listForeignNationalsByVisaExpiration, searchNationalNames,
} from 'graphql/customQueries';
import {
  getPayload, lower, notNullFilter,
} from 'shared/utilities';
import { DICTIONARY, sleep } from 'shared/constants';
import { resolvers } from 'shared/enums';
import { format as formatDate, parseISO } from 'date-fns';
import { updateForeignNational } from 'graphql/customPersonContainerQueries';
import { client } from 'lib/graphql-client';
import { GraphQLResult } from '@aws-amplify/api';
import { post } from 'aws-amplify/api';

type RelationshipTypeMap = {
  [x in FamilyRelationshipType]: {
    label: string;
    inverse: FamilyRelationshipType;
  };
};

// A subset of the VisaStatus, dictating the possible values of a Foreign
// National’s immigration status. Keep this enum and array in sync.

export const nationalVisaStatusOptions = [
  VisaStatus.AOS_APPLICANT,
  VisaStatus.B_1,
  VisaStatus.B_2,
  VisaStatus.E_1,
  VisaStatus.E_2,
  VisaStatus.E_3_STATUS,
  VisaStatus.E_3D_STATUS,
  VisaStatus.F_1,
  VisaStatus.F_2,
  VisaStatus.GLOBAL_PROCESS,
  VisaStatus.H_1B,
  VisaStatus.H_1B1_STATUS,
  VisaStatus.H_2A,
  VisaStatus.H_3,
  VisaStatus.H_4_STATUS,
  VisaStatus.I,
  VisaStatus.J_1,
  VisaStatus.L_2,
  VisaStatus.L_1A,
  VisaStatus.L_1B,
  VisaStatus.LPR,
  VisaStatus.O_1,
  VisaStatus.O_3,
  VisaStatus.P_1,
  VisaStatus.P_3,
  VisaStatus.R_1,
  VisaStatus.TD_STATUS,
  VisaStatus.TN_STATUS,
  VisaStatus.US_CITIZEN,
  VisaStatus.NOT_IN_US,
  VisaStatus.A_1,
  VisaStatus.A_2,
  VisaStatus.A_3,
  VisaStatus.ASYLEE,
  VisaStatus.C_1,
  VisaStatus.CW_1,
  VisaStatus.CW_2,
  VisaStatus.D,
  VisaStatus.DACA,
  VisaStatus.E_1D,
  VisaStatus.E_2D,
  VisaStatus.ESTA,
  VisaStatus.G_1,
  VisaStatus.G_2,
  VisaStatus.G_3,
  VisaStatus.G_4,
  VisaStatus.G_5,
  VisaStatus.H_2B,
  VisaStatus.M_1,
  VisaStatus.M_2,
  VisaStatus.NATO_1,
  VisaStatus.NATO_2,
  VisaStatus.NATO_3,
  VisaStatus.NATO_4,
  VisaStatus.NATO_5,
  VisaStatus.NATO_6,
  VisaStatus.NATO_7,
  VisaStatus.O_2,
  VisaStatus.OTHER,
  VisaStatus.P_2,
  VisaStatus.P_4,
  VisaStatus.Q_1,
  VisaStatus.PAROLEE,
  VisaStatus.PENDING_ASYLEE,
  VisaStatus.R_2,
  VisaStatus.U_1,
  VisaStatus.U_2,
  VisaStatus.U_3,
  VisaStatus.U_4,
  VisaStatus.U_5,
  VisaStatus.T_1,
  VisaStatus.T_2,
  VisaStatus.T_3,
  VisaStatus.T_4,
  VisaStatus.TPS,
  VisaStatus.J_2,
  VisaStatus.K_1,
  VisaStatus.K_3,
  VisaStatus.PERM_GREEN_CARD,
  VisaStatus.L_1,
  VisaStatus.L_BLANKET,
];

export enum AOSFilingEligibilityEnum {
  YES = 'YES',
  NO = 'NO',
  UNDETERMINED = 'UNDETERMINED',
}

export enum NationalVisaStatusOptionsEnum {
  AOS_APPLICANT = 'AOS_APPLICANT',
  B_1 = 'B_1',
  B_2 = 'B_2',
  E_1 = 'E_1',
  E_2 = 'E_2',
  F_1 = 'F_1',
  F_2 = 'F_2',
  E_3_STATUS = 'E_3_STATUS',
  E_3D_STATUS = 'E_3D_STATUS',
  GLOBAL_PROCESS = 'GLOBAL_PROCESS',
  H_1B = 'H_1B',
  H_1B1_STATUS = 'H_1B1_STATUS',
  H_2A = 'H_2A',
  H_3 = 'H_3',
  H_4_STATUS = 'H_4_STATUS',
  I = 'I',
  J_1 = 'J_1',
  L_2 = 'L_2',
  L_1A = 'L_1A',
  L_1B = 'L_1B',
  LPR = 'LPR',
  O_1 = 'O_1',
  O_3 = 'O_3',
  P_1 = 'P_1',
  P_3 = 'P_3',
  R_1 = 'R_1',
  TD_STATUS = 'TD_STATUS',
  TN_STATUS = 'TN_STATUS',
  US_CITIZEN = 'US_CITIZEN',
  NOT_IN_US = 'NOT_IN_US',
  A_1 = 'A_1',
  A_2 = 'A_2',
  A_3 = 'A_3',
  ASYLEE = 'ASYLEE',
  C_1 = 'C_1',
  CW_1 = 'CW_1',
  CW_2 = 'CW_2',
  D = 'D',
  DACA = 'DACA',
  E_1D = 'E_1D',
  E_2D = 'E_2D',
  ESTA = 'ESTA',
  G_1 = 'G_1',
  G_2 = 'G_2',
  G_3 = 'G_3',
  G_4 = 'G_4',
  G_5 = 'G_5',
  H_2B = 'H_2B',
  M_1 = 'M_1',
  M_2 = 'M_2',
  NATO_1 = 'NATO_1',
  NATO_2 = 'NATO_2',
  NATO_3 = 'NATO_3',
  NATO_4 = 'NATO_4',
  NATO_5 = 'NATO_5',
  NATO_6 = 'NATO_6',
  NATO_7 = 'NATO_7',
  O_2 = 'O_2',
  OTHER = 'OTHER',
  P_2 = 'P_2',
  P_4 = 'P_4',
  Q_1 = 'Q_1',
  PAROLEE = 'PAROLEE',
  PENDING_ASYLEE = 'PENDING_ASYLEE',
  R_2 = 'R_2',
  U_1 = 'U_1',
  U_2 = 'U_2',
  U_3 = 'U_3',
  U_4 = 'U_4',
  U_5 = 'U_5',
  T_1 = 'T_1',
  T_2 = 'T_2',
  T_3 = 'T_3',
  T_4 = 'T_4',
  TPS = 'TPS',
  J_2 = 'J_2',
  K_1 = 'K_1',
  K_3 = 'K_3',
}

export interface Relative extends ForeignNational {
  id: string;
  isSource: boolean;
  relation: string;
  relationshipID: string;
  // Source is this type to relative
  relationshipType: FamilyRelationshipType | null;
}

export const relationshipTypeMap: RelationshipTypeMap = {
  [FamilyRelationshipType.AUNT_UNCLE]: {
    label: 'Aunt/Uncle',
    inverse: FamilyRelationshipType.NIECE_NEPHEW,
  },
  [FamilyRelationshipType.CHILD]: {
    label: 'Child',
    inverse: FamilyRelationshipType.PARENT,
  },
  [FamilyRelationshipType.CHILD_IN_LAW]: {
    label: 'Child-in-Law',
    inverse: FamilyRelationshipType.PARENT_IN_LAW,
  },
  [FamilyRelationshipType.CHILD_OF_GUARDIAN]: {
    label: 'Child of Guardian',
    inverse: FamilyRelationshipType.GUARDIAN,
  },
  [FamilyRelationshipType.COMMON_LAW_PARTNER]: {
    label: 'Common-Law Partner',
    inverse: FamilyRelationshipType.COMMON_LAW_PARTNER,
  },
  [FamilyRelationshipType.COUSIN]: {
    label: 'Cousin',
    inverse: FamilyRelationshipType.COUSIN,
  },
  [FamilyRelationshipType.FIANCE]: {
    label: 'Fiance',
    inverse: FamilyRelationshipType.FIANCE,
  },
  [FamilyRelationshipType.GRANDCHILD]: {
    label: 'Grandchild',
    inverse: FamilyRelationshipType.GRANDPARENT,
  },
  [FamilyRelationshipType.GRANDPARENT]: {
    label: 'Grandparent',
    inverse: FamilyRelationshipType.GRANDCHILD,
  },
  [FamilyRelationshipType.GUARDIAN]: {
    label: 'Guardian',
    inverse: FamilyRelationshipType.CHILD_OF_GUARDIAN,
  },
  [FamilyRelationshipType.LTDP]: {
    label: 'Long-Term Domestic Partner',
    inverse: FamilyRelationshipType.LTDP,
  },
  [FamilyRelationshipType.NIECE_NEPHEW]: {
    label: 'Niece/Nephew',
    inverse: FamilyRelationshipType.AUNT_UNCLE,
  },
  [FamilyRelationshipType.PARENT]: {
    label: 'Parent',
    inverse: FamilyRelationshipType.CHILD,
  },
  [FamilyRelationshipType.PARENT_IN_LAW]: {
    label: 'Parent-in-Law',
    inverse: FamilyRelationshipType.CHILD_IN_LAW,
  },
  [FamilyRelationshipType.PRIOR_LTDP]: {
    label: 'Prior Long-Term Domestic Partner',
    inverse: FamilyRelationshipType.PRIOR_LTDP,
  },
  [FamilyRelationshipType.SIBLING]: {
    label: 'Sibling',
    inverse: FamilyRelationshipType.SIBLING,
  },
  [FamilyRelationshipType.SPOUSE]: {
    label: 'Spouse',
    inverse: FamilyRelationshipType.SPOUSE,
  },
  [FamilyRelationshipType.SPOUSE_DECEASED]: {
    label: 'Deceased Spouse',
    inverse: FamilyRelationshipType.PRIOR_SPOUSE_OF_DECEASED,
  },
  [FamilyRelationshipType.SPOUSE_DIVORCED]: {
    label: 'Divorced Spouse',
    inverse: FamilyRelationshipType.SPOUSE_DIVORCED,
  },
  [FamilyRelationshipType.PRIOR_SPOUSE_OF_DECEASED]: {
    label: 'Prior Spouse of Deceased',
    inverse: FamilyRelationshipType.SPOUSE_DECEASED,
  },
  [FamilyRelationshipType.STEP_CHILD]: {
    label: 'Step Child',
    inverse: FamilyRelationshipType.STEP_PARENT,
  },
  [FamilyRelationshipType.STEP_PARENT]: {
    label: 'Step Parent',
    inverse: FamilyRelationshipType.STEP_CHILD,
  },
};

class NationalService {
  /**
   * Make source an administrator of target.
   * @param sourceID - The Dynamo ID of source
   * @param targetID - The Dynamo ID of target
   */
  addAdministrator = async (sourceID: string, targetID: string) => {
    const response = (await client.graphql({
      query: customGetForeignNational,
      variables: { id: targetID },
    })) as GraphQLResult<GetForeignNationalQuery>;

    if (!response?.data?.getForeignNational) {
      throw new Error();
    }

    const currentAdmins = response.data.getForeignNational.relativeAdmins || [];

    return client.graphql({
      query: mutations.updateForeignNational,
      variables: {
        input: {
          id: targetID,
          relativeAdmins: [...currentAdmins, sourceID],
        },
      },
    });
  };

  getForeignNational = async (id: string) => {
    try {
      const response = (await client.graphql({
        query: customGetForeignNational,
        variables: { id },
      })) as GraphQLResult<GetForeignNationalQuery>;

      if (!response?.data?.getForeignNational) {
        throw new Error();
      }

      return response?.data?.getForeignNational;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error >>>>>', error);

      return error;
    }
  };

  updateFamilyRelationships = async (
    nationalID: string,
    newRelationsNationalIDs: Array<string>, // IDs of nationals
    deletedRelationshipIDs: Array<string>, // IDs of Family Relationships
    deletedRelationshipNationalIDs: Array<string>, // IDs of nationals
  ) => {
    const body = {
      nationalID,
      newRelationsNationalIDs,
      deletedRelationshipIDs,
      deletedRelationshipNationalIDs,
    };

    const payload = await getPayload(body);

    if (deletedRelationshipNationalIDs.length > 0) {
      await this.deleteAdministrator(deletedRelationshipNationalIDs, nationalID);
    }

    const { body: relBody } = await post({ apiName: 'utilitiesapi', path: '/familyRelationship', options: payload as any }).response;

    return relBody.json() as unknown as { newRelationshipIDs: string[] };
  };

  deleteAdministrator = async (deletedRelationshipNationalIDs: string[], nationalId: string) => {
    await Promise.all(
      deletedRelationshipNationalIDs.map(async (id) => {
        const fnData = (await client.graphql({
          query: customGetForeignNational,
          variables: { id },
        })) as GraphQLResult<GetForeignNationalQuery>;

        const currentAdmins = fnData?.data?.getForeignNational?.relativeAdmins || [];
        const newRelativeAdmins = currentAdmins.filter((adminId) => adminId !== nationalId);

        await client.graphql({
          query: mutations.updateForeignNational,
          variables: {
            input: {
              id,
              relativeAdmins: [...newRelativeAdmins],
            },
          },
        });
      }),
    );
  };

  changeRelationship = async (
    relationshipID: string,
    sourceID: string,
    targetID: string,
    relationship: FamilyRelationshipType | null,
  ) => {
    try {
      await client.graphql({
        query: mutations.updateFamilyRelationship,
        variables: {
          input: {
            id: relationshipID,
            type: relationship,
            sourceID,
            targetID,
          },
        },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  formatImmigrationStatus = (national?: ForeignNational): string => {
    if (!national?.immigrationStatus?.status) {
      return '';
    }

    const { expiration, status } = national.immigrationStatus;

    let returnString = resolvers.VisaStatus(status);

    if (expiration) {
      returnString += ` | Exp. ${formatDate(parseISO(expiration), 'M/d/yy')}`;
    }

    return returnString;
  };

  getImmigrationInfo = (national?: ForeignNational): any => {
    if (!national?.immigrationStatus?.status) {
      return '';
    }

    const { expiration, status } = national.immigrationStatus;

    const visaStatus = resolvers.VisaStatus(status);
    let visaExpiration = '';
    if (expiration) {
      visaExpiration = `Exp. ${formatDate(parseISO(expiration), 'M/d/yy')}`;
    }

    return {
      visaStatus,
      visaExpiration,
    };
  };

  get: Get<ForeignNational> = async (id) => {
    const response = (await client.graphql({
      query: queries.getForeignNational,
      variables: { id },
    })) as GraphQLResult<GetForeignNationalQuery>;

    return (response.data?.getForeignNational as ForeignNational) || null;
  };

  /**
   *
   * @param national A foreign National
   * @returns An array of IDs for nationals for whom the argument national
   *          is an administrator.
   */
  getDependentIDs = (national: ForeignNational | null | undefined): Array<string> => {
    if (!national) return [];

    const sourceRelatives = (national.relativesAsSource?.items?.filter(
      (item) => item?.target?.relativeAdmins?.includes(national.id!),
    ) || [] as Array<FamilyRelationship>).map((item) => item!.target!.id!);
    const targetRelatives = (
      national.relativesAsTarget?.items?.filter(
        (item) => item?.source?.relativeAdmins?.includes(national.id!),
      ) || ([] as Array<FamilyRelationship>)
    ).map((item) => item!.source!.id!);

    return [...sourceRelatives, ...targetRelatives];
  };

  /**
   *
   * @param nationalID
   * @returns An array of Relatives, which are the same as Foreign Nationals
   *          except they have a "relation" field that indicates the relation
   *          (if any) to the requesting national
   */
  getRelatives = async (nationalID: string): Promise<Array<Relative>> => {
    let response: GraphQLResult<GetRelativesQuery>;
    let national: ForeignNational | null | undefined;
    let relatives: Array<Relative> = [];

    try {
      response = (await client.graphql({
        query: getRelatives,
        variables: { id: nationalID },
      })) as GraphQLResult<GetRelativesQuery>;
      national = (response.data?.getForeignNational as ForeignNational) || null;
    } catch (err) {
      national = ((err as GraphQLResult<GetRelativesQuery>).data
        ?.getForeignNational as ForeignNational) || null;

      if (!national) {
        throw new Error();
      }
    } finally {
      const asSource = ((national?.relativesAsSource?.items || [])
        .filter(notNullFilter)
        .map((item: any) => ({
          ...item?.target,
          isSource: false,
          relation: this.getRelationshipLabel(item!.type || null, false),
          relationshipID: item.id,
          relationshipType: item!.type,
        })) || []) as Array<Relative>;

      const asTarget = ((national?.relativesAsTarget?.items || [])
        .filter(notNullFilter)
        .map((item: any) => ({
          ...item?.source,
          isSource: true,
          relation: this.getRelationshipLabel(item!.type || null, true),
          relationshipID: item.id,
          relationshipType: this.getInverseRelationship(item!.type),
        })) || []) as Array<Relative>;

      relatives = [...asSource, ...asTarget];
    }

    return relatives;
  };

  getInverseRelationship = (
    type: FamilyRelationshipType | null | undefined,
  ): FamilyRelationshipType | null => {
    if (!type) {
      return null;
    }

    return relationshipTypeMap[type].inverse;
  };

  getInverseRelationshipLabel = (
    type: FamilyRelationshipType | null | undefined,
  ): string => {
    const inverseType = this.getInverseRelationship(type);

    if (!inverseType) {
      return DICTIONARY.DefaultFamilyContact;
    }

    return relationshipTypeMap[inverseType].label;
  };

  getRelationshipLabel = (
    type: FamilyRelationshipType | null,
    relativeToSource?: boolean,
  ): string => {
    if (!type) {
      return DICTIONARY.DefaultFamilyContact;
    }

    if (relativeToSource) {
      return this.getInverseRelationshipLabel(type);
    }

    return relationshipTypeMap[type].label;
  };

  hasCompletedOnboarding = (
    onboardingSections?: NationalOnboardingSections,
  ): Boolean => {
    if (!onboardingSections) {
      return true;
    }

    const values = Object.values(onboardingSections);

    return !values.some((value) => !value);
  };

  onboardingSectionsDone = (
    onboardingSections?: NationalOnboardingSections,
  ): [number, number] => {
    if (!onboardingSections) {
      return [0, 0];
    }

    const values = Object.values(onboardingSections);

    return [values.filter((value) => value).length, values.length];
  };

  markOnboardingSectionDone = async (
    national: ForeignNational,
    section: NationalOnboardingSection,
    completedOnboardingQuestionsBy: string,
  ) => {
    try {
      const response = (await client.graphql({
        query: updateForeignNational,
        variables: {
          input: {
            id: national.id,
            onboardingSections: {
              ...national.onboardingSections,
              [section]: true,
            },
            completedOnboardingQuestionsBy,
          },
        },
      })) as GraphQLResult<ContainerUpdateForeignNationalMutation>;
      if (response.data?.updateForeignNational) {
        return response.data?.updateForeignNational;
      }

      throw new Error('Failure to update national.');
    } catch (err: any) {
      if (err.data?.updateForeignNational) {
        return err.data?.updateForeignNational;
      }
      // eslint-disable-next-line no-console
      console.warn(err);
      throw err;
    }
  };

  listForeignNationalsWithUpcomingVisaExpirations = async (person: Person): Promise<
  Array<ForeignNational>
  > => {
    let and = null;
    if (person.type === PersonType.Employer) {
      const or = person.companyIDs.map(
        (id: string) => ({ companyID: { eq: id } }),
      );
      and = { or };
    }

    const response = (await client.graphql({
      query: listForeignNationalsByVisaExpiration,
      variables: {
        gte: new Date().toISOString().split('T')[0],
        and,
      },
    })) as GraphQLResult<ListForeignNationalsByVisaExpirationQuery>;

    return (
      (response.data?.searchForeignNationals
        ?.items as Array<ForeignNational>) || []
    );
  };

  /**
   * Check whether a relationship exists between two foreign nationals.
   * @param idA
   * @param idB
   */
  relationshipExists = async (idA: string, idB: string): Promise<boolean> => {
    const relatives = await this.getRelatives(idA);

    return relatives.some((item) => item.id === idB);
  };

  /**
   * Remmove source as an administrator of target.
   * @param sourceID - The Dynamo ID of source
   * @param targetID - The Dynamo ID of target
   */
  removeAdministrator = async (sourceID: string, targetID: string) => {
    const response = (await client.graphql({
      query: queries.getForeignNational,
      variables: { id: targetID },
    })) as GraphQLResult<GetForeignNationalQuery>;

    if (!response?.data?.getForeignNational) {
      throw new Error();
    }

    const currentAdmins = response.data.getForeignNational.relativeAdmins || [];

    return client.graphql({
      query: mutations.updateForeignNational,
      variables: {
        input: {
          id: targetID,
          relativeAdmins: currentAdmins.filter((id) => id !== sourceID),
        },
      },
    });
  };

  searchByName: SearchByName<ForeignNational> = async (
    name?: string,
    maxCount?: number,
    archived?: ArchivedState,
  ) => {
    if (!name) {
      return [];
    }

    const chunks = lower(name).split(' ');

    const andArray: Array<SearchableForeignNationalFilterInput> = [
      {
        archived: {
          eq: archived || 'ACTIVE',
        },
      },
    ];

    chunks.forEach((chunk) => {
      andArray.push({ lowerFull: { wildcard: `*${chunk}*` } });
    });

    try {
      const response = (await client.graphql({
        query: searchNationalNames,
        variables: {
          and: andArray,
          limit: maxCount,
        },
      })) as GraphQLResult<SearchForeignNationalNamesQuery>;

      return (response.data?.searchForeignNationals?.items
        || []) as Array<ForeignNational>;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  updateJobDescription = (id: string, jobDescriptionID: string) => client.graphql({
    query: mutations.updateForeignNational,
    variables: {
      input: {
        id,
        jobDescriptionID,
      },
    },
  });

  updateOfferedSalary = (id: string, amount: number, period?: CurrencyPeriod | null) => client.graphql({
    query: mutations.updateForeignNational,
    variables: {
      input: {
        id,
        offeredSalary: amount ? {
          currency: Currency.USD,
          amount,
          period: period || CurrencyPeriod.ANNUAL,
        } : null,
      },
    },
  });

  /* Get all Active Nationals */

  getActiveNationals = async (isMH: boolean, companies: Company[]) => {
    const nationalsArray: ForeignNational[] = [];
    let nextToken = null;
    const limit = 1000;

    try {
      if (isMH) {
        do {
          const response = await (client.graphql({
            query: queries.listForeignNationals,
            variables: {
              filter: { archived: { eq: ArchivedState.ACTIVE } },
              limit,
              nextToken,
            },
          })) as GraphQLResult<ListForeignNationalsQuery>;

          await sleep(1000);

          if (response.data?.listForeignNationals) {
            const { items } = response.data.listForeignNationals;
            nextToken = response.data.listForeignNationals.nextToken;

            nationalsArray.push(...(items as ForeignNational[] || []));
          }
        } while (nextToken);
      } else if (companies?.length) {
        const orArray: { companyID: { eq: string; }; }[] = [];

        companies.forEach((company: Company) => {
          orArray.push({ companyID: { eq: company.id } });
        });

        do {
          const response = await (client.graphql({
            query: queries.listForeignNationals,
            variables: {
              filter: {
                and: [{ archived: { eq: ArchivedState.ACTIVE } }],
                or: orArray,
              },
              limit,
              nextToken,
            },
          })) as GraphQLResult<ListForeignNationalsQuery>;

          if (response.data?.listForeignNationals) {
            const { items } = response.data.listForeignNationals;
            nextToken = response.data.listForeignNationals.nextToken;

            nationalsArray.push(...(items as ForeignNational[] || []));
          }
        } while (nextToken);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('ERROR >>>>>', error);
    }

    return nationalsArray?.length;
  };
}

export default new NationalService();
